import { useContext, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import { IDocument } from '../../../types/my-types';
import DeleteIcon from '@mui/icons-material/Delete';
import { AppContext } from '../../../App';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGlynt from '../../hooks/useGlynt';
import { getIdFromUrl } from '../../../utils/getIdFromUrl';
import { ITrainingSet } from '../../../types/my-types';
import { useTrainingSetUpdate } from '../../hooks/useTrainingSetUpdate';
import { useTheme } from '@mui/material/styles';

export interface ITrainingSetDeleteDocumentProps {
  documentLabel: string;
  documentId: string;
  trainingSet: ITrainingSet;
  tooltip?: string;
}
const TrainingSetDeleteDocument = ({
  documentLabel,
  documentId,
  trainingSet,
  tooltip = 'Delete Document',
}: ITrainingSetDeleteDocumentProps) => {
  const { dataPoolId, isStaff, setToast } = useContext(AppContext);
  const [open, setOpen] = useState(false);

  const client = useQueryClient();
  const glynt = useGlynt();
  const theme = useTheme();

  const { mutateAsync: updateTrainingSet, isPending: isLoadingUpdateTrainingSet } =
    useTrainingSetUpdate({
      onSuccess: () => {
        client.invalidateQueries({
          queryKey: ['documents']
        });
        client.invalidateQueries({
          queryKey: ['trainingSet']
        });
        client.invalidateQueries({
          queryKey: ['trainingSets']
        });
        client.invalidateQueries({
          queryKey: ['trainingSetsByDocument']
        });
        setToast({
          variant: 'success',
          open: true,
          message: 'Deleted',
        });
      },
      onError: () => {
        setToast({
          variant: 'error',
          open: true,
          message: 'Sorry, failed to delete document.',
        });
      },
    });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip placement="top" title={tooltip} disableFocusListener disableTouchListener>
        <DeleteIcon
          data-cy={`btn-manage-table-delete-${documentLabel}`}
          onClick={handleClickOpen}
          style={{ color: theme.palette.custom.lightGreen, fontSize: '21px', cursor: 'pointer' }}
        />
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleCloseConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirmation'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this document?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmation}>Cancel</Button>
          <Button
            onClick={() => {
              let documents = trainingSet.documents.filter(
                (doc: string) => getIdFromUrl(doc) !== documentId
              );

              updateTrainingSet({
                trainingSetId: trainingSet.id,
                data: { documents: documents },
              });
            }}
            autoFocus
            data-cy="btn-training-set-delete-modal-yes"
            disabled={isLoadingUpdateTrainingSet}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TrainingSetDeleteDocument;
